import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import SplashScreen from "./components/SplashScreen";
import AdminFlashModal from "./components/AdminFlashModal";
import GlobalWSConnector from "./components/GlobalWSConnector";
import ReferralToastListener from "./components/ReferralToastListener";
import Navbar from "./components/Navbar";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import RidesPage from "./pages/RidesPage";
import VehiclesPage from "./pages/VehiclesPage";
import PassengerDashboard from "./pages/PassengerDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ImpersonateLanding from "./pages/ImpersonateLanding";
import ExpressPage from "./pages/ExpressPage";
import DriverExpressDashboard from "./pages/DriverExpressDashboard";
import PartnerDashboard from "./pages/PartnerDashboard";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import TermsPage from "./pages/TermsPage";
import { useKeepAwake } from "./hooks/useKeepAwake";
import { useAppLifecycle } from "./hooks/useAppLifecycle";
import AdminExpressLiveDashboard from "./components/admin/AdminExpressLiveDashboard";
import ImpersonationBanner from "./components/ImpersonationBanner";
import BookingAlertBanner from "./components/BookingAlertBanner";
import ExpressCallListener from "./components/ExpressCallListener";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <Loader2 className="w-8 h-8 animate-spin text-[#0A2540]" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Admin has access to all dashboards
  if (user.role === "admin") return children;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
    </>
  );
}

function DashboardLayout({ children }) {
  return (
    <>
      <ImpersonationBanner />
      <BookingAlertBanner />
      <ExpressCallListener />
      <Navbar />
      {children}
    </>
  );
}

const PASSENGER_ROLES = ["passenger"];
const DRIVER_ROLES = ["driver"];
const ADMIN_ROLES = ["admin"];
const PARTNER_ROLES = ["partner"];

function AppRoutes() {
  const { user } = useAuth();
  // Global "don't let the app fall asleep" strategy:
  //   - Screen wake lock while a user is signed in (Chrome Android, Edge,
  //     Safari 16.4+). Prevents the screen dimming that leads to tab freeze
  //     and forced reload when the user comes back.
  //   - App lifecycle: persist the last route so a killed-tab restore
  //     brings the user back where they were, and broadcast "resumed" so
  //     hooks can silently refresh their data instead of forcing a reload.
  useKeepAwake({ enabled: !!user });
  useAppLifecycle();
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><LandingPage /></PublicLayout>} />
      <Route path="/welcome" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/rides" element={<PublicLayout><RidesPage /></PublicLayout>} />
      <Route path="/vehicles" element={<PublicLayout><VehiclesPage /></PublicLayout>} />
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/passenger" element={
        <ProtectedRoute roles={PASSENGER_ROLES}>
          <DashboardLayout><PassengerDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/driver" element={
        <ProtectedRoute roles={DRIVER_ROLES}>
          <DashboardLayout><DriverDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute roles={ADMIN_ROLES}>
          <DashboardLayout><AdminDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/impersonate" element={<ImpersonateLanding />} />
      <Route path="/express" element={<ExpressPage />} />
      <Route path="/admin/express-live" element={
        <ProtectedRoute roles={["admin"]}>
          <DashboardLayout><AdminExpressLiveDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/driver/express" element={
        <ProtectedRoute roles={["driver"]}>
          <DashboardLayout><DriverExpressDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/partner" element={
        <ProtectedRoute roles={PARTNER_ROLES}>
          <DashboardLayout><PartnerDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Splash screen: show on every app start unless the user reloads inside an
  // already-open session (we use sessionStorage so a page refresh will replay it).
  const [splashDone, setSplashDone] = useState(() => {
    try { return sessionStorage.getItem("trigo:splash-done") === "1"; }
    catch (_e) { return false; }
  });
  const handleSplashDone = () => {
    try { sessionStorage.setItem("trigo:splash-done", "1"); } catch (_e) { /* noop */ }
    setSplashDone(true);
  };
  return (
    <BrowserRouter>
      <AuthProvider>
        {!splashDone && <SplashScreen onDone={handleSplashDone} />}
        {/* Global side-effects — active on every route as soon as the user is
            authenticated. GlobalWSConnector keeps a single WS open; the
            AdminFlashModal listens for admin_flash events dispatched by that
            connector and renders a centered modal wherever the user is. */}
        <GlobalWSConnector />
        <ReferralToastListener />
        <AdminFlashModal />
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
